import { zodResolver } from "@hookform/resolvers/zod";
import { SettingsIcon, SquarePenIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import z from "zod";

import {
  Column,
  RichTextDisplay,
  RichTextEditorInput,
  Row,
  Text,
} from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useCreateAddCandidacyArtifacts } from "@/hooks/createAdds/useCreateAddCandidacyArtifacts";
import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useRefetchCandidacySummaryChanges } from "@/hooks/refetches/useRefetchCandidacySummaryChanges";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CandidacyInfoResponse } from "@/types";

const executiveSummarySchema = z.object({
  summary: z.string().min(1, { message: "Required" }),
});

const EXECUTIVE_SUMMARY_TEMPLATE =
  "<p><strong>Technical Fit:</strong></p><p><strong>Practical Fit:</strong></p><p><strong>Cultural Fit:</strong></p>";

const ExecutiveSummaryEditor = ({
  candidacyInfo,
  onDone,
}: {
  candidacyInfo: CandidacyInfoResponse;
  onDone: () => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateCandidacy } = useUpdateCandidacy();
  const setCandidacyInfo = useSetCandidacyInfo();
  const refetchSummaryChanges = useRefetchCandidacySummaryChanges();
  // Create
  const { mutate: generateCandidacyArtifacts } =
    useCreateAddCandidacyArtifacts();

  /* React Hooks */
  const errorToast = useErrorToast();
  const form = useForm<z.infer<typeof executiveSummarySchema>>({
    defaultValues: {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      summary: candidacyInfo.summary || EXECUTIVE_SUMMARY_TEMPLATE,
    },
    resolver: zodResolver(executiveSummarySchema),
  });

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof executiveSummarySchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Save exec summary",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    updateCandidacy(
      {
        candidacyId: candidacyInfo.id,
        updateCandidacyRequest: {
          summary: data.summary,
        },
      },
      {
        onSuccess: () => {
          setCandidacyInfo({
            candidacyId: candidacyInfo.id,
            summary: data.summary,
          });
          // guac logs a summary change on every candidacy update, so the
          // activity rail has a new event to pick up.
          refetchSummaryChanges({ candidacyId: candidacyInfo.id });
          onDone();
          regeneratePacket();
        },
      },
    );
  };

  // Regenerate packet so exec summary isn't stale
  const regeneratePacket = () => {
    if (!candidacyInfo.execSummaryPacketUrl) return;

    generateCandidacyArtifacts(
      {
        candidacyId: candidacyInfo.id,
        talentId: candidacyInfo.talentId ?? null,
      },
      {
        onError: () => {
          errorToast({
            description:
              "Summary saved, but the exec summary packet couldn't be rebuilt.",
          });
        },
      },
    );
  };

  /* Render */
  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormLabel className="font-semibold text-slate-700">
          Executive Summary
        </FormLabel>
        <FormField
          control={form.control}
          name="summary"
          render={({ field }) => (
            <FormItem className="text-slate-700">
              <FormControl>
                <RichTextEditorInput
                  mode={{
                    onChange: field.onChange,
                    type: "html",
                    value: field.value,
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Row className="items-center justify-end gap-2">
          <Button onClick={onDone} size="sm" type="button" variant="secondary">
            Cancel
          </Button>
          <Button size="sm" type="submit">
            Save
          </Button>
        </Row>
      </form>
    </Form>
  );
};

const ExecutiveSummaryDisplay = ({
  onEdit,
  summary,
}: {
  onEdit?: () => void;
  summary: string;
}) => {
  return (
    <Column
      className="rounded-xl border border-primary-300 bg-primary-300 pb-[18px] pl-5 pr-4 pt-3.5"
      // Double-click enters edit mode, matching the profile sections.
      onDoubleClick={onEdit}
    >
      <Row className="mb-2 items-center justify-between">
        <Row className="items-center gap-1.5">
          <SettingsIcon className="h-3.5 w-3.5 text-primary-900" />
          <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-primary-900">
            Executive Summary
          </Text>
        </Row>
        {onEdit ? (
          <Button
            className="h-auto p-0 hover:bg-transparent"
            onClick={onEdit}
            size="icon"
            variant="ghost"
          >
            <SquarePenIcon className="h-4 w-4 text-slate-700" />
          </Button>
        ) : null}
      </Row>
      <RichTextDisplay
        className="text-sm font-normal leading-relaxed text-[#121212] dark:text-[#FAFAFA]"
        richText={summary}
      />
    </Column>
  );
};

// Candidacy-level executive summary, rendered above the profile body. Adding one
// starts from the rail's add button; once written it stays editable here.
export const ExecutiveSummarySection = ({
  candidacyInfo,
  isEditing,
  setIsEditing,
}: {
  candidacyInfo: CandidacyInfoResponse;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  /* Context */
  const { isReadOnly } = useUserSheet();

  /* Render */
  const render = () => {
    if (isEditing && !isReadOnly) {
      return (
        <ExecutiveSummaryEditor
          candidacyInfo={candidacyInfo}
          onDone={() => {
            setIsEditing(false);
          }}
        />
      );
    }

    if (!candidacyInfo.summary) return null;

    return (
      <ExecutiveSummaryDisplay
        onEdit={
          isReadOnly
            ? undefined
            : () => {
                setIsEditing(true);
              }
        }
        summary={candidacyInfo.summary}
      />
    );
  };

  return render();
};
